import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    checkUser();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        // Fast path: Check role from JWT metadata first
        const userRole = session.user.user_metadata?.role;
        
        if (userRole === 'inspector') {
          await supabase.auth.signOut();
          setUser(null);
          setIsAuthenticated(false);
          setAuthError({
            type: 'access_denied',
            message: 'Access denied. Inspectors cannot access this app.'
          });
        } else if (userRole === 'writer' || userRole === 'admin') {
          setUser(session.user);
          setIsAuthenticated(true);
        } else {
          // Fallback to database check if no role in metadata
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .single();
          
          if (profile && profile.role !== 'inspector') {
            setUser(session.user);
            setIsAuthenticated(true);
          } else {
            await supabase.auth.signOut();
            setUser(null);
            setIsAuthenticated(false);
            if (profile && profile.role === 'inspector') {
              setAuthError({
                type: 'access_denied',
                message: 'Access denied. Inspectors cannot access this app.'
              });
            }
          }
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setIsLoadingAuth(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkUser = async () => {
    try {
      setIsLoadingAuth(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Fast path: Check role from JWT metadata first
        const userRole = session.user.user_metadata?.role;
        
        // If role is in metadata and it's inspector, block immediately
        if (userRole === 'inspector') {
          await supabase.auth.signOut();
          setUser(null);
          setIsAuthenticated(false);
          setAuthError({
            type: 'access_denied',
            message: 'Access denied. Inspectors cannot access this app.'
          });
        } 
        // If role is in metadata and it's writer/admin, allow immediately (fast path)
        else if (userRole === 'writer' || userRole === 'admin') {
          setUser(session.user);
          setIsAuthenticated(true);
        }
        // Fallback: If no role in metadata, check database (slower path)
        else {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .single();
          
          if (profileError || !profile || profile.role === 'inspector') {
            await supabase.auth.signOut();
            setUser(null);
            setIsAuthenticated(false);
            if (profile && profile.role === 'inspector') {
              setAuthError({
                type: 'access_denied',
                message: 'Access denied. Inspectors cannot access this app.'
              });
            }
          } else {
            setUser(session.user);
            setIsAuthenticated(true);
          }
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      setAuthError(error);
    } finally {
      setIsLoadingAuth(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    
    // Check role from JWT metadata first (fast path)
    if (data.user) {
      const userRole = data.user.user_metadata?.role;
      
      if (userRole === 'inspector') {
        await supabase.auth.signOut();
        throw new Error('Access denied. Inspectors cannot access this app.');
      }
      
      // If role not in metadata, check database and update metadata
      if (!userRole || (userRole !== 'writer' && userRole !== 'admin')) {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', data.user.id)
          .single();
        
        if (profileError || !profile) {
          await supabase.auth.signOut();
          throw new Error('Profile not found. Please contact support.');
        }
        
        if (profile.role === 'inspector') {
          await supabase.auth.signOut();
          throw new Error('Access denied. Inspectors cannot access this app.');
        }
        
        // Update user metadata with role for future fast path
        try {
          await supabase.auth.updateUser({
            data: { role: profile.role }
          });
        } catch (updateError) {
          console.log('Could not update user metadata:', updateError);
          // Don't throw - user can still login
        }
      }
    }
    
    return data;
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.full_name || '',
          role: 'writer', // Store role in user metadata
        }
      }
    });
    
    if (error) throw error;
    
    // Profile will be created automatically by database trigger
    // The trigger reads from raw_user_meta_data to set role and full_name
    
    return data;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
  };

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/ResetPassword`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAuthenticated(false);
  };

  const navigateToLogin = () => {
    // Implement your login navigation
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      authError,
      signIn,
      signUp,
      signInWithGoogle,
      resetPassword,
      updatePassword,
      logout,
      navigateToLogin,
      checkUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
